import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Import axios directly
import { attemptAPI, examAPI } from '../../services/api'; // Keep the API services for other calls
import InternshipInterface from './InternshipInterface';

// Configure axios to use the same base URL as the API service
axios.defaults.baseURL = 'https://api.khwanzay.school/api/v1';
axios.defaults.withCredentials = true; // Enable credentials if needed

const PublicInternshipInterface = () => {
  const [questions, setQuestions] = useState([]);
  const [exam, setExam] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(2160); // 36 minutes
  const [examProgress, setExamProgress] = useState(0);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showFullScreenQuestion, setShowFullScreenQuestion] = useState(true);
  const [showQuestionNavigator, setShowQuestionNavigator] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [participantName, setParticipantName] = useState(''); // Add participant name state

  // Get testId from URL
  const getUrlParameter = (name) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  const testId = getUrlParameter('testId') || window.location.pathname.split('/').pop();

  // Fetch exam data
  useEffect(() => {
    const fetchExamData = async () => {
      try {
        setLoading(true);
        
        // Create a temporary attempt for the public exam
        // For public exams, we pass null as bookingId and add isPublic flag
        const attemptResponse = await attemptAPI.startAttempt(testId, null);
        console.log('Attempt created:', attemptResponse.data);
        setAttemptId(attemptResponse.data.attempt.id);
        
        // Fetch the actual exam questions
        const examResponse = await examAPI.getExamById(testId);
        console.log('Exam data fetched:', examResponse.data);
        setExam(examResponse.data.exam);
        setQuestions(examResponse.data.questions);
        setTimeLeft(2160); // Set to 36 minutes (2160 seconds)
        setLoading(false);
      } catch (err) {
        console.error('Error fetching exam data:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (testId) {
      fetchExamData();
    }
  }, [testId]);

  // Handle answer selection
  const handleAnswerSelect = (questionId, answerData) => {
    console.log('Setting answer for question:', questionId, 'with data:', answerData);
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: answerData
      };
      console.log('Updated answers state:', newAnswers);
      return newAnswers;
    });
    
    // Submit answer immediately for public exams
    if (attemptId) {
      attemptAPI.submitAnswer(attemptId, questionId, answerData)
        .catch(err => {
          console.error('Error submitting answer:', err);
        });
    }
  };

  // Handle fill in the blank answers
  const handleFillInTheBlankAnswer = (questionId, blankAnswers) => {
    console.log('Setting fill in blank answer for question:', questionId, 'with data:', blankAnswers);
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: blankAnswers
      };
      console.log('Updated answers state:', newAnswers);
      return newAnswers;
    });
    
    // Submit answer immediately for public exams
    if (attemptId) {
      attemptAPI.submitAnswer(attemptId, questionId, blankAnswers)
        .catch(err => {
          console.error('Error submitting answer:', err);
        });
    }
  };

  // Handle participant name change
  const handleParticipantNameChange = (name) => {
    console.log('Participant name changed to:', name);
    setParticipantName(name);
  };

  // Toggle question flag
  const toggleQuestionFlag = (questionIndex) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionIndex)) {
        newSet.delete(questionIndex);
      } else {
        newSet.add(questionIndex);
      }
      return newSet;
    });
  };

  // Handle submit
  const handleSubmit = async (submittedParticipantName) => {
    try {
      console.log('Exam submitted with answers:', answers);
      console.log('Participant name:', submittedParticipantName);
      console.log('Attempt ID:', attemptId);
      console.log('State participant name:', participantName);
      
      // Use the submitted name or the state name
      const finalParticipantName = submittedParticipantName || participantName || 'Anonymous Participant';
      
      console.log('Final participant name being sent:', finalParticipantName);
      
      if (!attemptId) {
        throw new Error('No attempt ID found');
      }
      
      // Prepare the data to send
      const submissionData = {
        responses: answers,
        name: finalParticipantName
      };
      
      console.log('Full submission data being sent:', submissionData);
      
      // Submit the complete attempt with the participant name
      const response = await axios.post(`/exams/attempts/${attemptId}/complete`, submissionData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Exam submission response:', response.data);
      
      // Show success message and redirect
      alert('Thank you for completing the assessment!');
      
      // Redirect to a thank you page or show results
      window.location.href = `/exam/thanks/${attemptId}`;
    } catch (error) {
      console.error('Error submitting exam:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error request:', error.request);
      console.error('Error config:', error.config);
      alert('Error submitting exam. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading exam...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: 'red'
      }}>
        Error loading exam: {error}
      </div>
    );
  }

  return (
    <InternshipInterface
      showFullScreenQuestion={showFullScreenQuestion}
      setShowFullScreenQuestion={setShowFullScreenQuestion}
      questions={questions}
      timeLeft={timeLeft}
      examProgress={examProgress}
      flaggedQuestions={flaggedQuestions}
      toggleQuestionFlag={toggleQuestionFlag}
      answers={answers}
      handleAnswerSelect={handleAnswerSelect}
      handleFillInTheBlankAnswer={handleFillInTheBlankAnswer}
      handleSubmit={handleSubmit}
      showInstructions={showInstructions}
      toggleInstructions={setShowInstructions}
      showCalculator={showCalculator}
      setShowCalculator={setShowCalculator}
      showQuestionNavigator={showQuestionNavigator}
      setShowQuestionNavigator={setShowQuestionNavigator}
      exam={exam}
      isInternshipExam={true}
      isPublic={true}
      publicQuestions={questions}
      publicExam={exam}
      // Pass the participant name state and setter
      participantName={participantName}
      setParticipantName={handleParticipantNameChange}
    />
  );
};

export default PublicInternshipInterface;